import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ErrorHandlerService } from '../services/error-handler.service';

/**
 * Interceptor HTTP funcional encargado de la gestión global de errores de red.
 *
 * Centraliza la captura de respuestas fallidas emitidas por el backend. Su propósito principal
 * es delegar el manejo de estos errores al `ErrorHandlerService` para procesarlos de forma
 * uniforme (ej. mostrando notificaciones toast o registrando el fallo en consola).
 * * Contiene una exclusión estratégica para los errores `401 Unauthorized`: si este error
 * ocurre fuera del flujo de autenticación (login o refresco de token), el interceptor lo ignora
 * intencionadamente. Esto permite que el error "suba" intacto hasta el `authInterceptor`,
 * dándole a este último la oportunidad de pausar la petición y ejecutar el flujo de "refresh token"
 * de forma silenciosa, sin que el usuario vea un mensaje de error prematuro.
 *
 * @param req La petición HTTP original.
 * @param next El manejador que delega la petición al siguiente interceptor o al backend.
 * @returns Un `Observable` que emite el evento HTTP o propaga el error interceptado.
 * @throws Relanza el `HttpErrorResponse` original para mantener la cadena de interceptores activa
 * o permitir que los componentes individuales gestionen sus propios errores si lo necesitan.
 *
 * @example
 * // Es crucial el orden de registro en app.config.ts.
 * // El authInterceptor debe ir ANTES para que en la respuesta (flujo inverso) reciba el error relanzado:
 * provideHttpClient(withInterceptors([authInterceptor, errorInterceptor]))
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const errorHandler = inject(ErrorHandlerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isLoginRequest = req.url.includes('/login') || req.url.includes('/auth');
      const isRefreshRequest = req.url.includes('/refresh-token');

      // Si el error es 401 (caducado), y NO es el login, y NO es un fallo del propio refresco...
      // Hacemos la vista gorda. NO llamamos al errorHandler para no redirigir al login.
      // Simplemente lanzamos el error para que el auth.interceptor lo intercepte y lo arregle.
      if (error.status === 401 && !isLoginRequest && !isRefreshRequest) {
        return throwError(() => error);
      }

      // Para todos los demás errores (500, 404, validaciones, o si el refresh-token falla)
      // usamos tu servicio manejador de errores normal.
      errorHandler.handleHttpError(error);

      // Re-lanzamos el error por si algún componente necesita suscribirse a él
      return throwError(() => error);
    }),
  );
};
